import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import {SellersApi} from '@frontend/modules/public/seller_info/SellersApi'
import {ReviewsListItem} from '@frontend/modules/public/shared/reviews/ReviewsListItem'
import {ReviewsListRatingSummary} from '@frontend/modules/public/shared/reviews/ReviewsListRatingSummary'
import type {
    ReviewsListItemDataForAd,
    ReviewsListRatingBreakdownItemData,
} from '@frontend/modules/public/shared/reviews/ReviewsModuleTypes'
import {
    useEffect,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'
import {ReviewsListRatingBreakdown} from '../../shared/reviews/ReviewsListRatingBreakdown'
import type {SellerDetailsData} from '../SellerInfoModuleTypes'

interface Props {
    seller: SellerDetailsData
}

// Кол-во отзывов на страницу.
const pageLimit: number = 10

// Окно со списком отзывов о продавце.
export function SellerReviewsList(props: Props) {

    const {
        seller,
    } = props

    // Загружаемые данные.
    const [
        reviews,
        setReviews,
    ] = useState<ReviewsListItemDataForAd[]>([])

    const [
        isAllReviewsLoaded,
        setIsAllReviewsLoaded,
    ] = useState<boolean>(false)

    const [
        ratingBreakdown,
        setRatingBreakdown,
    ] = useState<ReviewsListRatingBreakdownItemData[]>([])

    // Ошибки и состояния активности интерактивных элементов.
    const [
        isLoading,
        setIsLoading,
    ] = useState<boolean>(true)

    const [
        loadingError,
        setLoadingError,
    ] = useState<boolean>(false)

    // Загрузка отзывов (страницами).
    const loadMoreReviews = (reload: boolean) => {
        setIsLoading(true)
        setLoadingError(false)
        SellersApi.getReviews(
            seller.id,
            pageLimit,
            reload ? 0 : reviews.length
        )
            .then(data => {
                setReviews(reviews => (reload ? data.records : reviews.concat(data.records)))
                setIsAllReviewsLoaded(data.records.length < pageLimit)
                setIsLoading(false)
                if (data.ratingBreakdown) {
                    setRatingBreakdown(data.ratingBreakdown)
                }
                if (!reload && data.records.length === 0) {
                    ToastService.info(trans().reviews.all_reviews_loaded)
                }
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error)
                setLoadingError(reviews.length === 0)
                setIsLoading(false)
            })
    }

    // Загрузка отзывов.
    useEffect(() => {
        loadMoreReviews(true)
    }, [seller.id])

    // Отправить событие просмотра отзывов продавца при монтировании.
    useEffect(() => {
        AnalyticsEventReporter.viewSellerReviews(seller.id)
    }, [])

    return (
        <div>
            <ReviewsListRatingSummary
                rating={seller.rating}
                reviewsCount={seller.reviewsCount}
            />
            <AsyncDataLoading
                loading={isLoading && reviews.length === 0}
                error={loadingError}
                onReload={() => loadMoreReviews(false)}
                render={() => {
                    if (reviews.length === 0) {
                        return (
                            <div className="py-6 px-5 text-center fs-5">
                                {trans().reviews.no_reviews}
                            </div>
                        )
                    }
                    return (
                        <div>
                            <ReviewsListRatingBreakdown
                                ratingBreakdown={ratingBreakdown}
                                reviewsCount={seller.reviewsCount}
                            />
                            <div className="mt-n2">
                                {reviews.map((review: ReviewsListItemDataForAd) => (
                                    <ReviewsListItem
                                        key={review.id}
                                        review={review}
                                        reviewedItemTitle={review.advertisement.title}
                                    />
                                ))}
                            </div>
                            <Collapse
                                show={!isAllReviewsLoaded}
                                showImmediately
                            >
                                <div className="flex-center pt-4">
                                    <Button
                                        color="gray"
                                        onClick={() => loadMoreReviews(false)}
                                        disabled={isLoading}
                                    >
                                        {trans().common.load_more_records}
                                    </Button>
                                </div>
                            </Collapse>
                        </div>
                    )
                }}
            />
        </div>
    )
}
